<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import BattlePlayback from '../shared/BattlePlayback.vue'
import { apiRequest, notifyTelegram, openSiblingGame, prepareTelegram } from '../shared/api.js'
import { battleOutcome, catalogMap, FACTION_NAMES, heroLabel, RARITY_NAMES } from '../shared/format.js'
import type {
  HeroTemplate,
  OwnedHero,
  RaidBattleResponse,
  RaidBootstrap,
  SummonResponse,
} from '../shared/types.js'

type Screen = 'summon' | 'squad' | 'battle' | 'heroes'

const state = ref<RaidBootstrap | null>(null)
const screen = ref<Screen>('summon')
const loadout = ref<string[]>([])
const stage = ref(1)
const loading = ref(true)
const busy = ref(false)
const error = ref('')
const notice = ref('')
const summonedHero = ref<OwnedHero | null>(null)
const battle = ref<RaidBattleResponse | null>(null)

const templates = computed(() => catalogMap(state.value?.catalog ?? []))
const selectedHeroes = computed(() => loadout.value
  .map(id => state.value?.heroes.find(hero => hero.instanceId === id))
  .filter((hero): hero is OwnedHero => Boolean(hero)))

function templateFor(hero: OwnedHero): HeroTemplate | undefined {
  return templates.value.get(hero.templateId)
}

function toggleHero(heroId: string): void {
  error.value = ''
  if (loadout.value.includes(heroId)) {
    loadout.value = loadout.value.filter(id => id !== heroId)
  } else if (loadout.value.length < 4) {
    loadout.value = [...loadout.value, heroId]
  } else {
    error.value = 'В отряде «Призыва» может быть ровно 4 героя.'
  }
}

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    const response = await apiRequest<RaidBootstrap>('/api/raid/bootstrap')
    state.value = response
    loadout.value = [...response.loadout]
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Не удалось загрузить «Призыв».'
  } finally {
    loading.value = false
  }
}

async function summon(): Promise<void> {
  if (!state.value) return
  busy.value = true
  error.value = ''
  notice.value = ''
  try {
    const response = await apiRequest<SummonResponse>('/api/raid/summons', { method: 'POST' })
    state.value.profile = response.profile
    state.value.heroes.push(response.hero)
    summonedHero.value = response.hero
    notice.value = `Призван: ${templateFor(response.hero)?.name ?? response.hero.templateId}`
    notifyTelegram('success')
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Призыв не удался.'
    notifyTelegram('error')
  } finally {
    busy.value = false
  }
}

async function saveSquad(): Promise<boolean> {
  if (loadout.value.length !== 4) {
    error.value = 'Выбери ровно 4 героев.'
    return false
  }
  busy.value = true
  error.value = ''
  try {
    await apiRequest('/api/raid/loadout', { method: 'PUT', body: { heroIds: loadout.value } })
    if (state.value) state.value.loadout = [...loadout.value]
    notice.value = 'Отряд сохранён.'
    notifyTelegram('success')
    return true
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Не удалось сохранить отряд.'
    return false
  } finally {
    busy.value = false
  }
}

async function startBattle(): Promise<void> {
  if (!state.value || !await saveSquad()) return
  busy.value = true
  error.value = ''
  notice.value = ''
  battle.value = null
  try {
    const response = await apiRequest<RaidBattleResponse>('/api/raid/battles', {
      method: 'POST',
      body: { stage: stage.value },
    })
    battle.value = response
    state.value.profile = response.profile
    state.value.heroes = response.heroes
    notifyTelegram(response.result.winner === 'player' ? 'success' : 'warning')
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Бой не удалось начать.'
  } finally {
    busy.value = false
  }
}

onMounted(() => {
  prepareTelegram()
  void load()
})
</script>

<template>
  <main class="app-shell raid-shell">
    <header class="topbar">
      <div>
        <p class="eyebrow">Пошаговая гача-стратегия</p>
        <h1>Призыв</h1>
      </div>
      <div v-if="state" class="wallet">
        <span class="pill">◆ {{ state.profile.summonStones }}</span>
        <span class="pill">● {{ state.profile.gold }}</span>
      </div>
    </header>

    <button v-if="state" class="secondary switch-game" type="button" @click="openSiblingGame(state.arenaUrl)">
      Перейти в «Прорыв» →
    </button>

    <p v-if="loading" class="panel">Загружаем общий склад героев…</p>
    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="notice" class="success">{{ notice }}</p>

    <template v-if="state && !loading">
      <section v-if="screen === 'summon'" class="panel">
        <h2>Призыв</h2>
        <p class="muted">Один призыв стоит {{ state.summon.stoneCost }} камня. Шансы фиксированы и видны до нажатия.</p>
        <div class="rates">
          <div class="rate"><strong>{{ state.summon.rates.common }}%</strong><span>Обычный</span></div>
          <div class="rate"><strong>{{ state.summon.rates.rare }}%</strong><span>Редкий</span></div>
          <div class="rate"><strong>{{ state.summon.rates.epic }}%</strong><span>Эпический</span></div>
        </div>
        <p v-if="state.profile.nextStoneSeconds !== null" class="muted">
          Следующий камень примерно через {{ Math.ceil(state.profile.nextStoneSeconds / 60) }} мин., накопление до {{ state.profile.timedStoneLimit }}.
        </p>
        <button class="primary summon-button" type="button" :disabled="busy || state.profile.summonStones < state.summon.stoneCost" @click="summon">
          {{ busy ? 'Камни светятся…' : `Призвать за ${state.summon.stoneCost} ◆` }}
        </button>
        <div v-if="summonedHero" class="summon-result">
          <div class="hero-token" :class="`faction-${templateFor(summonedHero)?.faction}`">
            {{ templateFor(summonedHero)?.initials }}
          </div>
          <div>
            <strong>{{ templateFor(summonedHero)?.name }}</strong>
            <span>{{ RARITY_NAMES[templateFor(summonedHero)?.rarity ?? 'common'] }}</span>
          </div>
        </div>
      </section>

      <section v-else-if="screen === 'squad'" class="panel">
        <h2>Отряд · {{ loadout.length }}/4</h2>
        <p class="muted">Очередь ходов зависит от скорости. Фракции образуют круг преимуществ.</p>
        <div class="hero-grid">
          <button
            v-for="hero in state.heroes"
            :key="hero.instanceId"
            class="hero-card"
            :class="{ selected: loadout.includes(hero.instanceId) }"
            type="button"
            @click="toggleHero(hero.instanceId)"
          >
            <span class="hero-token" :class="`faction-${templateFor(hero)?.faction}`">{{ templateFor(hero)?.initials }}</span>
            <span class="hero-info">
              <strong>{{ heroLabel(hero, templates) }}</strong>
              <small>{{ FACTION_NAMES[templateFor(hero)?.faction ?? 'ember'] }} · {{ RARITY_NAMES[templateFor(hero)?.rarity ?? 'common'] }}</small>
            </span>
          </button>
        </div>
        <button class="primary full-button" type="button" :disabled="busy || loadout.length !== 4" @click="saveSquad">Сохранить отряд</button>
      </section>

      <section v-else-if="screen === 'battle'" class="panel">
        <h2>Пошаговый бой</h2>
        <div class="form-row">
          <label for="stage">Этап сложности</label>
          <input id="stage" v-model.number="stage" type="number" min="1" max="20" />
        </div>
        <div class="selected-strip">
          <span v-for="hero in selectedHeroes" :key="hero.instanceId" class="mini-token" :class="`faction-${templateFor(hero)?.faction}`">
            {{ templateFor(hero)?.initials }}
          </span>
        </div>
        <button class="primary full-button" type="button" :disabled="busy || loadout.length !== 4" @click="startBattle">
          {{ busy ? 'Сервер считает бой…' : 'В бой' }}
        </button>
        <div v-if="battle" class="battle-wrap">
          <p><strong>Награда:</strong> {{ battle.rewards.stones }} ◆ · {{ battle.rewards.gold }} ● · {{ battle.rewards.heroExperience }} опыта каждому</p>
          <BattlePlayback :events="battle.result.events" mode="raid" />
        </div>
      </section>

      <section v-else class="panel">
        <h2>Общий склад · {{ state.heroes.length }}</h2>
        <div class="hero-grid">
          <div v-for="hero in state.heroes" :key="hero.instanceId" class="hero-card">
            <span class="hero-token" :class="`faction-${templateFor(hero)?.faction}`">{{ templateFor(hero)?.initials }}</span>
            <span class="hero-info">
              <strong>{{ heroLabel(hero, templates) }}</strong>
              <small>Опыт: {{ hero.experience }} / {{ hero.experienceForNextLevel ?? 'MAX' }}</small>
            </span>
          </div>
        </div>
        <h3>Последние бои</h3>
        <div class="history">
          <div v-for="item in state.battles" :key="item.id" class="history-row">
            <span>Бой №{{ item.id }} · {{ battleOutcome(item.winner) }}</span>
            <span>+{{ item.rewardGold }} ●</span>
          </div>
          <p v-if="state.battles.length === 0" class="muted">Боёв пока не было.</p>
        </div>
      </section>
    </template>

    <nav class="bottom-nav" aria-label="Навигация «Призыва»">
      <button class="nav-button" :class="{ active: screen === 'summon' }" type="button" @click="screen = 'summon'">Призыв</button>
      <button class="nav-button" :class="{ active: screen === 'squad' }" type="button" @click="screen = 'squad'">Отряд</button>
      <button class="nav-button" :class="{ active: screen === 'battle' }" type="button" @click="screen = 'battle'">Бой</button>
      <button class="nav-button" :class="{ active: screen === 'heroes' }" type="button" @click="screen = 'heroes'">Склад</button>
    </nav>
  </main>
</template>

<style scoped>
.raid-shell { background: radial-gradient(circle at top right, #4d2535 0, #17111f 34%, #0d1020 76%); }
.summon-button, .full-button { inline-size: 100%; margin-block-start: 14px; }
.summon-result { display: flex; align-items: center; justify-content: center; gap: 12px; margin-block-start: 16px; padding: 16px; border: 1px solid #8b79ff; border-radius: 16px; background: #282049; }
.summon-result span { display: block; margin-block-start: 3px; color: #b8b2d2; }
.selected-strip { display: flex; justify-content: center; gap: 8px; min-block-size: 42px; }
.mini-token { display: grid; place-items: center; inline-size: 38px; aspect-ratio: 1; border-radius: 50%; font-weight: 900; }
.battle-wrap { margin-block-start: 14px; }
</style>
